import { useState } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Label, NativeSelect, NativeSelectOption, ScrollArea, Separator } from 'shared-ui';

import { useClient } from '@/clients/useClient';
import { fetchAllPoisForDownload } from '@/api/poisApi';
import { fetchAllPoiPipelinesForDownload } from '@/api/poiPipelinesApi';
import { exportPois } from '@/utils/poiExport';
import { exportPoisWithPipelines } from '@/utils/poiPipelinesExport';
import { type ExportFormat } from '@/utils/exportWorkbook';
import { selectEntityExportRows, type ExportStatusSelection } from '@/utils/poiExportFilters';
import { useDownloadFileName } from '@/hooks/useDownloadFileName';
import { DateField } from '@/components/shared/DateField';
import { DownloadFileNameField } from '@/components/shared/DownloadFileNameField';
import { DownloadFormatField } from '@/components/shared/DownloadFormatField';
import { PanelCloseButton, SlidingPanel } from '@/components/shared/SlidingPanel';
import { ToggleRow } from '@/components/shared/ToggleRow';

import { LEAK_VISIBILITY_OPTIONS } from '../utils/mapFilters';
import type { LeakVisibilityOption } from '../utils/mapFilters';
import { DEFAULT_ENTITY_MAP_FILTER_STATE } from '../utils/entityMapFilters';
import { leakMarkerImageId, poiMarkerImageId } from '../utils/entityMarkerArt';
import { enabledLeakStatuses, enabledPoiStatuses } from '../utils/entityStatus';
import type { LeakEntityStatus, PoiEntityStatus } from '../utils/entityStatus';
import { StatusMarkerIcon } from './StatusMarkerIcon';

export interface DownloadPanelProps {
    isOpen: boolean;
    onClose: () => void;
    /**
     * Seed for the status switches when the panel opens: the committed Filters state when
     * filters are active, null otherwise (⇒ everything on). Never derived from the Layers
     * entity toggles — those are display declutter, not a statement of data intent.
     */
    seedStatuses: ExportStatusSelection | null;
}

// Same 19.38×24px icon box as the Filters panel's status rows.
const STATUS_ICON_CLASS = 'h-6 w-[19.38px] shrink-0 object-contain';

function DownloadPanelContent({ onClose, seedStatuses }: Omit<DownloadPanelProps, 'isOpen'>) {
    const { selectedClient } = useClient();
    const {
        fileName,
        setFileName,
        error: fileNameError,
        isValid: fileNameValid,
    } = useDownloadFileName('Points of Interest', selectedClient?.name, { prefill: false });
    const [format, setFormat] = useState<ExportFormat>('xlsx');
    const [isExporting, setIsExporting] = useState(false);

    // The download has its own selection, independent of the map once open: changing
    // anything here only affects the exported file. The status switches start from the
    // committed Filters state when filters are active ("download what I'm looking at"),
    // else all-on; the panel content remounts per open, so the seed is applied fresh
    // each time. The registry defaults keep disabled placeholder statuses off.
    const [poiStatuses, setPoiStatuses] = useState<Record<PoiEntityStatus, boolean>>(() => ({
        ...(seedStatuses?.poiStatuses ?? DEFAULT_ENTITY_MAP_FILTER_STATE.poiStatuses),
    }));
    const [leakStatuses, setLeakStatuses] = useState<Record<LeakEntityStatus, boolean>>(() => ({
        ...(seedStatuses?.leakStatuses ?? DEFAULT_ENTITY_MAP_FILTER_STATE.leakStatuses),
    }));
    const [searchedOnly, setSearchedOnly] = useState(false);
    // Adds the associated-pipelines breakdown worksheet. Off by default: it is an extra
    // dataset most downloads do not need, and it costs a second request.
    const [includePipelines, setIncludePipelines] = useState(false);
    const [leakVisibility, setLeakVisibility] = useState<LeakVisibilityOption>('all');
    const [dateFrom, setDateFrom] = useState<string | null>(null);
    const [dateTo, setDateTo] = useState<string | null>(null);

    const dateRangeError = dateFrom && dateTo && dateFrom > dateTo ? 'From date must be before To date.' : null;
    const canDownload = selectedClient != null && !isExporting && fileNameValid && dateRangeError == null;

    async function handleDownload() {
        // canDownload encodes selectedClient != null, so TS narrows it to non-null below.
        if (!canDownload) return;

        setIsExporting(true);
        const toastId = toast.loading('Preparing download', {
            description: 'Fetching your POIs & Leaks. This may take a moment for large datasets.',
        });

        try {
            const items = await fetchAllPoisForDownload(selectedClient.id);

            const rows = selectEntityExportRows(
                items,
                { poiStatuses, leakStatuses },
                { searchedOnly, leakVisibility, dateFrom, dateTo },
            );

            // The name is already validated; the extension is appended by the export.
            // With Pipelines on, the stored associations are fetched and joined into a
            // second worksheet (a second CSV file for the CSV format).
            const downloaded = includePipelines
                ? await exportPoisWithPipelines(
                      rows,
                      await fetchAllPoiPipelinesForDownload(selectedClient.id),
                      fileName.trim(),
                      format,
                  )
                : await exportPois(rows, fileName.trim(), format);

            if (downloaded) {
                toast.success('Download ready', {
                    id: toastId,
                    description: 'Your POIs & Leaks file has been downloaded.',
                });
            } else {
                toast.info('No data to export', {
                    id: toastId,
                    description: 'No POIs or Leaks matched the current selection.',
                });
            }
        } catch {
            toast.error('Download failed', {
                id: toastId,
                description: 'Something went wrong preparing the download. Please try again.',
            });
        } finally {
            setIsExporting(false);
        }
    }

    return (
        <>
            {/* Header — fixed 80px height (h-20), does not scroll */}
            <div className="flex h-20 shrink-0 items-center justify-between px-6">
                <h2 className="text-lg leading-7 font-black tracking-normal uppercase md:text-2xl md:leading-8">
                    Download Data
                </h2>
                <PanelCloseButton onClick={onClose} aria-label="Close download panel" />
            </div>

            <Separator />

            {/* Scrollable form body. All controls here are download-only — they affect the
                exported file, never what is shown on the map. */}
            <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-6 px-6 py-4">
                    <div>
                        <p className="text-foreground mb-2 text-sm leading-5 font-medium">
                            Point of Interest (POI) Status
                        </p>
                        {enabledPoiStatuses().map((def) => (
                            <ToggleRow
                                key={def.status}
                                label={def.label}
                                icon={
                                    <StatusMarkerIcon
                                        imageId={poiMarkerImageId(def.status)}
                                        className={STATUS_ICON_CLASS}
                                    />
                                }
                                checked={poiStatuses[def.status]}
                                onCheckedChange={(enabled) => {
                                    setPoiStatuses((prev) => ({ ...prev, [def.status]: enabled }));
                                }}
                            />
                        ))}
                    </div>

                    <div>
                        <p className="text-foreground mb-2 text-sm leading-5 font-medium">Leak Status</p>
                        {enabledLeakStatuses().map((def) => (
                            <ToggleRow
                                key={def.status}
                                label={def.label}
                                icon={
                                    <StatusMarkerIcon
                                        imageId={leakMarkerImageId(def.status)}
                                        className={STATUS_ICON_CLASS}
                                    />
                                }
                                checked={leakStatuses[def.status]}
                                onCheckedChange={(enabled) => {
                                    setLeakStatuses((prev) => ({ ...prev, [def.status]: enabled }));
                                }}
                            />
                        ))}
                    </div>

                    {/* These two rows have no section heading, so they read as section titles
                        themselves: the Leak Status heading's colour and weight, flush with
                        it rather than inset like the rows beneath a heading. */}
                    <div>
                        <ToggleRow
                            label="Searched"
                            className="pl-0"
                            labelClassName="text-foreground font-medium"
                            checked={searchedOnly}
                            onCheckedChange={setSearchedOnly}
                        />
                        <ToggleRow
                            label="Pipelines"
                            className="pl-0"
                            labelClassName="text-foreground font-medium"
                            checked={includePipelines}
                            onCheckedChange={setIncludePipelines}
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label
                            htmlFor="download-leak-visibility"
                            className="text-foreground text-sm leading-5 font-medium"
                        >
                            Leak Visibility
                        </Label>
                        <NativeSelect
                            id="download-leak-visibility"
                            value={leakVisibility}
                            onChange={(e) => {
                                setLeakVisibility(e.target.value as LeakVisibilityOption);
                            }}
                            className="panel-select text-muted-foreground w-full text-sm leading-5 font-normal tracking-normal"
                        >
                            {LEAK_VISIBILITY_OPTIONS.map((o) => (
                                <NativeSelectOption key={o.value} value={o.value}>
                                    {o.label}
                                </NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </div>

                    {/* Date range — a record is included when its search or surveyed date falls in range.
                        Either bound may be left blank for an open-ended range. */}
                    <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                            <DateField
                                id="download-date-from"
                                label="From"
                                placeholder="Choose Date"
                                value={dateFrom}
                                onChange={setDateFrom}
                                side="top"
                            />
                            <DateField
                                id="download-date-to"
                                label="To"
                                placeholder="Choose Date"
                                value={dateTo}
                                onChange={setDateTo}
                                side="top"
                            />
                        </div>
                        {dateRangeError && <p className="text-destructive text-xs">{dateRangeError}</p>}
                    </div>

                    <DownloadFormatField id="download-format" value={format} onChange={setFormat} />

                    <DownloadFileNameField
                        id="download-filename"
                        value={fileName}
                        error={fileNameError}
                        placeholder={`E.g ${selectedClient?.name ?? 'Client Name'} POIs`}
                        onChange={setFileName}
                    />
                </div>
            </ScrollArea>

            {/* Sticky footer — always visible, never scrolls */}
            <div className="shrink-0 space-y-2 border-t px-6 py-4">
                <Button
                    className="h-10 w-full cursor-pointer"
                    type="button"
                    onClick={() => void handleDownload()}
                    disabled={!canDownload}
                >
                    <Download className="size-4" strokeWidth={2} />
                    Download
                </Button>
                <Button
                    variant="ghost"
                    type="button"
                    className="panel-close-btn text-primary h-10 w-full cursor-pointer"
                    onClick={onClose}
                >
                    Cancel
                </Button>
            </div>
        </>
    );
}

export function DownloadPanel(props: DownloadPanelProps) {
    // SlidingPanel keeps children mounted while closed, so the content is remounted (via
    // key) each time the panel OPENS, re-applying the seed so the switches always reflect
    // the filters committed at open time. The epoch bumps on the open transition only —
    // keying on the raw open flag would also remount at close, visibly blanking the form
    // during the slide-out animation. (The compare-and-adjust-during-render form is the
    // sanctioned derived-state pattern; an effect would paint one frame stale.)
    const [openEpoch, setOpenEpoch] = useState(0);
    const [wasOpen, setWasOpen] = useState(props.isOpen);
    if (props.isOpen !== wasOpen) {
        setWasOpen(props.isOpen);
        if (props.isOpen) setOpenEpoch((epoch) => epoch + 1);
    }

    return (
        <SlidingPanel
            isOpen={props.isOpen}
            onClose={props.onClose}
            ariaLabel="Download POIs and Leaks"
            title="Download"
            widthClassName="w-[377px]"
        >
            <DownloadPanelContent key={openEpoch} onClose={props.onClose} seedStatuses={props.seedStatuses} />
        </SlidingPanel>
    );
}
